import { useMemo, useEffect, useState } from 'react';
import {
  Typography,
  Button,
  Tooltip,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  FileDownload as FileDownloadIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { Unit, Lease } from '@urbanmix-tech/shared-js';
import { deleteLease } from '@/data';
import { DraftLeaseCard } from '@/components/BuildingTabs/DraftLeaseCard';
import {
  exportMultipleUnitsToSpreadsheet,
  calculateLeaseMetrics,
  calculateProposedMetricsFromDraft,
  UnitPricingData,
} from '@/helpers/spreadsheetExport';
import { getLongNumberStr } from '@/helpers/common';
import { PricingWizardContextValue } from '../types';
import { CreateShareDialog } from '../dialogs/CreateShareDialog';
import {
  setHighlightedUnits,
  setBuildingVisual,
} from '@/features/project/projectSlice';
import { ColorGradeVisualizer } from '@urbanmix-tech/um-embedded';
import { colorScales } from '@/components/BuildingTabs/BuildingVisualizers';

interface ReviewStepProps {
  units: Unit[];
  leases: Lease[];
  draftLeases: Lease[];
  wizard: PricingWizardContextValue;
  projectId: string;
  buildingId: number;
}

export function ReviewStep({
  units,
  leases,
  draftLeases,
  wizard,
  projectId,
  buildingId,
}: ReviewStepProps) {
  const dispatch = useDispatch();
  const { state, actions, pricedUnitsCount, totalSelectedCount } = wizard;
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [showDifferenceAsPercent, setShowDifferenceAsPercent] = useState(true);

  // Create maps for quick lookup
  const unitMap = useMemo(() => {
    const map = new Map<string, Unit>();
    units.forEach((unit) => map.set(unit.id, unit));
    return map;
  }, [units]);

  const currentLeaseMap = useMemo(() => {
    const map = new Map<string, Lease>();
    leases.forEach((lease) => {
      const existing = map.get(lease.unitId);
      if (
        !existing ||
        new Date(lease.leaseStartDate) > new Date(existing.leaseStartDate)
      ) {
        map.set(lease.unitId, lease);
      }
    });
    return map;
  }, [leases]);

  // Filter draft leases to only those created in this wizard session
  const wizardDraftLeases = useMemo(() => {
    return draftLeases.filter((draft) => state.draftLeaseIds.has(draft.id));
  }, [draftLeases, state.draftLeaseIds]);

  // Highlight units with draft leases in the building visualization
  useEffect(() => {
    if (wizardDraftLeases.length === 0) {
      dispatch(setHighlightedUnits([]));
      dispatch(setBuildingVisual({ apartmentData: [], visualizer: undefined }));
      return;
    }

    // Create visualizer based on rent increase percentage
    const rentIncreases: number[] = [];
    wizardDraftLeases.forEach((draft) => {
      const currentLease = currentLeaseMap.get(draft.unitId);
      if (currentLease && currentLease.monthlyRent > 0) {
        const increase = ((draft.monthlyRent - currentLease.monthlyRent) / currentLease.monthlyRent) * 100;
        rentIncreases.push(increase);
      }
    });

    const minIncrease = Math.min(...rentIncreases, 0);
    const maxIncrease = Math.max(...rentIncreases, 10);
    const visualizer = new ColorGradeVisualizer(colorScales.default, minIncrease, maxIncrease);

    // Build apartment data for visualization
    const apartmentData = wizardDraftLeases.map((draft) => {
      const currentLease = currentLeaseMap.get(draft.unitId);
      const increase = currentLease && currentLease.monthlyRent > 0
        ? ((draft.monthlyRent - currentLease.monthlyRent) / currentLease.monthlyRent) * 100
        : 0;
      return {
        apartmentId: draft.unitId,
        value: increase,
        label: `${increase >= 0 ? '+' : ''}${increase.toFixed(1)}%`,
      };
    });

    dispatch(setBuildingVisual({ apartmentData, visualizer }));

    // Highlight after a short delay to ensure model is ready
    setTimeout(() => {
      dispatch(setHighlightedUnits(apartmentData));
    }, 500);

    return () => {
      dispatch(setHighlightedUnits([]));
    };
  }, [wizardDraftLeases, currentLeaseMap, dispatch]);

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (wizardDraftLeases.length === 0) {
      return null;
    }

    let totalCurrentRent = 0;
    let totalProposedRent = 0;
    let totalConcessions = 0;
    let validCount = 0;

    wizardDraftLeases.forEach((draft) => {
      const currentLease = currentLeaseMap.get(draft.unitId);
      if (currentLease) {
        totalCurrentRent += currentLease.monthlyRent;
        totalProposedRent += draft.monthlyRent;
        totalConcessions += draft.totalConcessionAmount || 0;
        validCount++;
      }
    });

    const avgCurrentRent = validCount > 0 ? totalCurrentRent / validCount : 0;
    const avgProposedRent = validCount > 0 ? totalProposedRent / validCount : 0;
    const avgConcessions = validCount > 0 ? totalConcessions / validCount : 0;
    const avgRentIncrease =
      avgCurrentRent > 0
        ? ((avgProposedRent - avgCurrentRent) / avgCurrentRent) * 100
        : 0;

    return {
      totalUnits: validCount,
      avgCurrentRent,
      avgProposedRent,
      avgConcessions,
      avgRentIncrease,
      totalCurrentRent,
      totalProposedRent,
      totalConcessions,
    };
  }, [wizardDraftLeases, currentLeaseMap]);

  // Handle export to spreadsheet
  const handleExportToSpreadsheet = async () => {
    if (wizardDraftLeases.length === 0) return;

    const unitsData: UnitPricingData[] = [];

    wizardDraftLeases.forEach((draftLease) => {
      const unit = unitMap.get(draftLease.unitId);
      const currentLease = currentLeaseMap.get(draftLease.unitId);

      if (!unit || !currentLease) return;

      const currentMetrics = calculateLeaseMetrics(unit, currentLease);
      if (!currentMetrics) return;

      const proposedMetrics = calculateProposedMetricsFromDraft(
        unit,
        currentMetrics,
        draftLease
      );

      unitsData.push({
        unit,
        currentLease,
        currentMetrics,
        proposedMetrics,
      });
    });

    if (unitsData.length > 0) {
      await exportMultipleUnitsToSpreadsheet(
        unitsData,
        units,
        currentLeaseMap,
        leases
      );
    }
  };

  // Handle delete draft lease
  const handleDeleteDraft = async (leaseId: string) => {
    try {
      await deleteLease(leaseId);
      // The lease will be removed from Redux via subscription
    } catch (error) {
      console.error('Error deleting draft lease:', error);
    }
  };

  // Handle edit - go back to pricing step
  const handleEditDraft = (draft: Lease) => {
    // Find the unit index in selected units
    const unitIndex = wizard.selectedUnits.findIndex(
      (u) => u.id === draft.unitId
    );
    if (unitIndex >= 0) {
      actions.setCurrentSingleUnitIndex(unitIndex);
      actions.setPricingMode('single');
      actions.setActiveStep(1); // Go back to pricing step
    }
  };

  return (
    <div className="review-step h-full flex flex-col px-8 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Typography variant="h6">Review Draft Leases</Typography>
          <Typography variant="body2" color="textSecondary">
            Review your proposed pricing before approval
          </Typography>
        </div>
        {wizardDraftLeases.length > 0 && (
          <div className="flex gap-2 items-center">
            <ToggleButtonGroup
              value={showDifferenceAsPercent ? 'percent' : 'dollar'}
              exclusive
              onChange={(_, value) => {
                if (value !== null) {
                  setShowDifferenceAsPercent(value === 'percent');
                }
              }}
              size="small"
            >
              <ToggleButton value="percent">Change (%)</ToggleButton>
              <ToggleButton value="dollar">Change ($)</ToggleButton>
            </ToggleButtonGroup>
            {/* <Tooltip title="Share with external reviewer for approval">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ShareIcon />}
                onClick={() => setShareDialogOpen(true)}
              >
                Share for Review
              </Button>
            </Tooltip> */}
            <Tooltip title="Export all draft leases to Excel">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FileDownloadIcon />}
                onClick={handleExportToSpreadsheet}
              >
                Export to Excel
              </Button>
            </Tooltip>
          </div>
        )}
      </div>

      {/* Summary stats */}
      {summaryStats && (
        <Paper className="mb-6 p-4 bg-zinc-800/70" elevation={0}>
          <Typography variant="subtitle2" className="mb-3 font-bold">
            Summary Statistics
          </Typography>
          <div className="grid grid-cols-5 gap-4">
            <div>
              <Typography variant="caption" color="textSecondary">
                Units Priced
              </Typography>
              <Typography variant="h6">{summaryStats.totalUnits}</Typography>
            </div>
            <div>
              <Typography variant="caption" color="textSecondary">
                Avg Current Rent
              </Typography>
              <Typography variant="h6">
                ${getLongNumberStr(Math.round(summaryStats.avgCurrentRent))}
              </Typography>
            </div>
            <div>
              <Typography variant="caption" color="textSecondary">
                Avg Proposed Rent
              </Typography>
              <Typography variant="h6" color="primary">
                ${getLongNumberStr(Math.round(summaryStats.avgProposedRent))}
              </Typography>
            </div>
            <div>
              <Typography variant="caption" color="textSecondary">
                Avg Rent Increase
              </Typography>
              <Typography
                variant="h6"
                color={summaryStats.avgRentIncrease >= 0 ? 'success.main' : 'error.main'}
              >
                {summaryStats.avgRentIncrease >= 0 ? '+' : ''}
                {summaryStats.avgRentIncrease.toFixed(1)}%
              </Typography>
            </div>
            <div>
              <Typography variant="caption" color="textSecondary">
                Avg Concessions
              </Typography>
              <Typography variant="h6">
                ${getLongNumberStr(Math.round(summaryStats.avgConcessions))}
              </Typography>
            </div>
          </div>
        </Paper>
      )}

      {/* Draft lease list */}
      {wizardDraftLeases.length > 0 ? (
        <div className="flex-1 overflow-auto">
          <div className="space-y-4">
            {wizardDraftLeases.map((draftLease) => {
              const draftUnit = unitMap.get(draftLease.unitId);
              const currentLease = currentLeaseMap.get(draftLease.unitId);
              if (!draftUnit || !currentLease) return null;

              return (
                <DraftLeaseCard
                  key={draftLease.id}
                  unit={draftUnit}
                  currentLease={currentLease}
                  draftLease={draftLease}
                  onDelete={handleDeleteDraft}
                  onEdit={handleEditDraft}
                  showDifferenceAsPercent={showDifferenceAsPercent}
                />
              );
            })}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Typography variant="body1" color="textSecondary" className="mb-2">
              No draft leases created yet
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Go back to the pricing step to create draft leases for your selected
              units
            </Typography>
            <Button
              variant="outlined"
              className="mt-4"
              onClick={() => actions.setActiveStep(1)}
            >
              Back to Pricing
            </Button>
          </div>
        </div>
      )}

      {/* Share Dialog */}
      <CreateShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        projectId={projectId}
        buildingId={buildingId}
        leaseIds={wizardDraftLeases.map((l) => l.id)}
      />
    </div>
  );
}
